/*
 *
 * AdminDashboard
 *
 * Landing screen for the gated /admin section: a growth snapshot (from
 * /api/v3/admin/growth) plus the section grid. Shipped sections link to their
 * route (Users, GIF monitor, Device status, Settings), Sidekiq links out to the
 * rails-mounted Rack UI, and any not-yet-built section (Growth) renders a
 * disabled "coming soon" placeholder.
 */

import React from "react";
import { Link } from "react-router-dom";
import Loader from "components/Loader";
import { useAdminGrowth } from "../api/queries";
import { sidekiqUrl } from "../sidekiq";
import {
  Wrapper,
  Grid,
  Card,
  CardLink,
  Metric,
  MetricLabel,
} from "./styledComponents";

// Sections of the superadmin panel. A `ready` section links to its `to` route;
// the rest render as disabled placeholders until their PR lands.
const SECTIONS = [
  { key: "users", label: "Users", ready: true, to: "/admin/users" },
  { key: "gifs", label: "GIF Monitor", ready: true, to: "/admin/gifs" },
  { key: "devices", label: "Device Status", ready: true, to: "/admin/devices" },
  { key: "growth", label: "Growth", ready: true, to: "/admin/growth" },
  { key: "settings", label: "Settings & Terms", ready: true, to: "/admin/settings" },
];

const GrowthSnapshot = () => {
  const { data, isLoading, isError } = useAdminGrowth();

  if (isLoading) return <Loader size="small" />;
  // Surface the failure rather than silently rendering zeros — a 403 here
  // means the role guard let a non-admin through, which we want to see.
  if (isError) return <MetricLabel>Growth metrics unavailable</MetricLabel>;

  // GET /api/v3/admin/growth returns a FLAT object — channel_count / gif_count /
  // share_count (plus image_count, per-type share counts, and by-month arrays).
  // Verified against Api::V3::Admin::GrowthController#index, which is the source
  // of truth. (NOT a nested *_metrics shape — that openapi drift was corrected.)
  const growth = data || {};
  return (
    <Grid>
      <Card>
        <Metric>{growth.channel_count ?? "—"}</Metric>
        <MetricLabel>Channels</MetricLabel>
      </Card>
      <Card>
        <Metric>{growth.gif_count ?? "—"}</Metric>
        <MetricLabel>GIFs</MetricLabel>
      </Card>
      <Card>
        <Metric>{growth.share_count ?? "—"}</Metric>
        <MetricLabel>Shares</MetricLabel>
      </Card>
    </Grid>
  );
};

const AdminDashboard = () => {
  // Sidekiq is the rails-mounted Rack UI (link-out, not a React route). Hidden
  // if the API root is unset/unparseable so we never render a broken link.
  const sidekiq = sidekiqUrl();

  return (
    <Wrapper>
      <h1>Admin</h1>
      <p className="admin-dashboard__intro">
        Superadmin tools. More sections land as they ship.
      </p>

      <GrowthSnapshot />

      <Grid className="admin-dashboard__sections">
        {SECTIONS.map((section) =>
          section.ready ? (
            <CardLink as={Link} to={section.to} key={section.key}>
              <MetricLabel>{section.label}</MetricLabel>
            </CardLink>
          ) : (
            <Card key={section.key} $disabled aria-disabled="true">
              <MetricLabel>{section.label}</MetricLabel>
              <span className="admin-dashboard__coming-soon">Coming soon</span>
            </Card>
          )
        )}

        {sidekiq && (
          <CardLink
            as="a"
            href={sidekiq}
            target="_blank"
            rel="noreferrer noopener"
          >
            <MetricLabel>
              Sidekiq <span aria-hidden="true">↗</span>
            </MetricLabel>
            <span className="admin-dashboard__external">Background jobs</span>
          </CardLink>
        )}
      </Grid>
    </Wrapper>
  );
};

export default AdminDashboard;
